/**
 * Web push subscription API (PWA Stage 2).
 */

import { api } from './client';

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function vapidPublicKey(): string | undefined {
  return import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
}

/** Base64url VAPID key → the BufferSource PushManager wants. */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  // Explicit ArrayBuffer backing: TS 5.7's DOM types reject ArrayBufferLike
  // (SharedArrayBuffer) for applicationServerKey.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/**
 * Request permission, subscribe this device, register with the server.
 * Throws with a friendly message when permission is denied.
 */
export async function enablePush(): Promise<void> {
  const key = vapidPublicKey();
  if (!key) throw new Error('Push notifications are not configured');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(
      'Notifications are blocked. You can enable them in your browser or system settings.'
    );
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key),
  });

  const json = subscription.toJSON();
  await api.post('/push/subscribe', {
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
  });
}

/** Unsubscribe this device (server first, then browser). */
export async function disablePush(): Promise<void> {
  const subscription = await getExistingSubscription();
  if (!subscription) return;
  try {
    await api.delete('/push/subscribe', { data: { endpoint: subscription.endpoint } });
  } finally {
    await subscription.unsubscribe();
  }
}
